import React, { useEffect, useMemo, useState } from 'react';
import { FaPlus, FaSave, FaSpinner, FaTrash } from 'react-icons/fa';
import spinGameService from '../../services/spinGameService';
import { useToast } from '../Shared/Toast';

const emptyRule = { prizeAmount: 0, probabilityPercent: 0, sortOrder: 1, isActive: true };
const getErrorMessage = (err) => {
  const data = err.response?.data;
  if (typeof data === 'string') return data;
  if (data?.message) return data.message;
  if (data?.title) return data.title;
  return err.message || 'Something went wrong.';
};

const AdminSpinGameSettings = () => {
  const { success, error } = useToast();
  const [settings, setSettings] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const stats = useMemo(() => {
    if (!settings) return { totalProbability: 0, expectedPayout: 0, userReturn: 0, platformMargin: 0 };

    const activeRules = (settings.prizeRules || []).filter((rule) => rule.isActive);
    const totalProbability = activeRules.reduce((sum, rule) => sum + Number(rule.probabilityPercent || 0), 0);
    const expectedPayout = activeRules.reduce(
      (sum, rule) => sum + (Number(rule.prizeAmount || 0) * Number(rule.probabilityPercent || 0)) / 100,
      0
    );
    const entryFee = Number(settings.entryFee || 0);
    const userReturn = entryFee > 0 ? (expectedPayout / entryFee) * 100 : 0;

    return {
      totalProbability,
      expectedPayout,
      userReturn,
      platformMargin: 100 - userReturn,
    };
  }, [settings]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsData, transactionData] = await Promise.all([
        spinGameService.getSettings(),
        spinGameService.getAllTransactions(),
      ]);
      setSettings(settingsData);
      setTransactions(transactionData);
    } catch (err) {
      error('Spin settings load failed', getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (field, value) => {
    setSettings((current) => ({ ...current, [field]: value }));
  };

  const updateRule = (index, field, value) => {
    setSettings((current) => ({
      ...current,
      prizeRules: current.prizeRules.map((rule, ruleIndex) =>
        ruleIndex === index ? { ...rule, [field]: value } : rule
      ),
    }));
  };

  const addRule = () => {
    setSettings((current) => ({
      ...current,
      prizeRules: [
        ...current.prizeRules,
        { ...emptyRule, sortOrder: current.prizeRules.length + 1 },
      ],
    }));
  };

  const removeRule = (index) => {
    setSettings((current) => ({
      ...current,
      prizeRules: current.prizeRules
        .filter((_, ruleIndex) => ruleIndex !== index)
        .map((rule, ruleIndex) => ({ ...rule, sortOrder: ruleIndex + 1 })),
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        ...settings,
        entryFee: Number(settings.entryFee),
        dailySpinLimit: settings.dailySpinLimit === '' ? null : Number(settings.dailySpinLimit),
        prizeRules: settings.prizeRules.map((rule, index) => ({
          ...rule,
          prizeAmount: Number(rule.prizeAmount),
          probabilityPercent: Number(rule.probabilityPercent),
          sortOrder: index + 1,
        })),
      };

      const saved = await spinGameService.saveSettings(payload);
      setSettings(saved);
      success('Spin settings saved', 'New odds are active.');
    } catch (err) {
      error('Save failed', getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center text-gray-600">
        <FaSpinner className="mr-2 animate-spin" /> Loading spin settings...
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Spin Game Settings</h1>
          <p className="mt-2 text-gray-600">Set entry fee, prize chances, daily limit, and active status.</p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || stats.totalProbability > 100}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-5 py-3 font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-400"
        >
          {saving ? <FaSpinner className="mr-2 animate-spin" /> : <FaSave className="mr-2" />}
          Save Settings
        </button>
      </div>

      <div className="mb-6 grid gap-4 md:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Expected payout</p>
          <p className="text-2xl font-bold text-gray-900">Rs {stats.expectedPayout.toFixed(2)}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">User return</p>
          <p className="text-2xl font-bold text-gray-900">{stats.userReturn.toFixed(2)}%</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <p className="text-sm text-gray-500">Platform margin</p>
          <p className="text-2xl font-bold text-gray-900">{stats.platformMargin.toFixed(2)}%</p>
        </div>
        <div className={`rounded-lg border p-4 shadow-sm ${stats.totalProbability > 100 ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
          <p className="text-sm text-gray-500">Total probability</p>
          <p className="text-2xl font-bold text-gray-900">{stats.totalProbability.toFixed(4)}%</p>
        </div>
      </div>

      <section className="mb-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <div className="grid gap-4 md:grid-cols-4">
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Game name</span>
            <input
              type="text"
              value={settings.name || ''}
              onChange={(event) => updateField('name', event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Entry fee</span>
            <input
              type="number"
              min="1"
              value={settings.entryFee}
              onChange={(event) => updateField('entryFee', event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-sm font-medium text-gray-700">Daily spin limit</span>
            <input
              type="number"
              min="0"
              value={settings.dailySpinLimit ?? ''}
              onChange={(event) => updateField('dailySpinLimit', event.target.value)}
              className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none"
            />
          </label>
          <label className="flex items-center gap-3 rounded-md border border-gray-200 px-3 py-2">
            <input
              type="checkbox"
              checked={Boolean(settings.isActive)}
              onChange={(event) => updateField('isActive', event.target.checked)}
              className="h-5 w-5"
            />
            <span className="font-medium text-gray-700">Active</span>
          </label>
        </div>
      </section>

      <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 p-5">
          <h2 className="text-lg font-semibold text-gray-900">Prize Rules</h2>
          <button
            type="button"
            onClick={addRule}
            className="inline-flex items-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
          >
            <FaPlus className="mr-2" /> Add Prize
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Prize</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Chance %</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">Active</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase text-gray-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {settings.prizeRules.map((rule, index) => (
                <tr key={index}>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min="0"
                      value={rule.prizeAmount}
                      onChange={(event) => updateRule(index, 'prizeAmount', event.target.value)}
                      className="w-40 rounded-md border border-gray-300 px-3 py-2"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min="0"
                      max="100"
                      step="0.0001"
                      value={rule.probabilityPercent}
                      onChange={(event) => updateRule(index, 'probabilityPercent', event.target.value)}
                      className="w-40 rounded-md border border-gray-300 px-3 py-2"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={Boolean(rule.isActive)}
                      onChange={(event) => updateRule(index, 'isActive', event.target.checked)}
                      className="h-5 w-5"
                    />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => removeRule(index)}
                      className="inline-flex items-center rounded-md border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      <FaTrash className="mr-2" /> Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-6 rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Recent Spins</h2>
        <div className="max-h-96 overflow-y-auto">
          {transactions.length === 0 ? (
            <p className="text-sm text-gray-500">No spin records yet.</p>
          ) : (
            transactions.slice(0, 50).map((transaction) => (
              <div key={transaction.spinTransactionId} className="grid grid-cols-2 gap-3 border-b border-gray-100 py-3 text-sm md:grid-cols-5">
                <span>#{transaction.spinTransactionId}</span>
                <span>User {transaction.userId}</span>
                <span>Entry Rs {Number(transaction.entryFee).toFixed(2)}</span>
                <span>Prize Rs {Number(transaction.prizeAmount).toFixed(2)}</span>
                <span className="text-gray-500">{new Date(transaction.createdAt).toLocaleString()}</span>
              </div>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminSpinGameSettings;
